import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Check, X } from 'lucide-react';
import searchService from '../../store/services/search.service';
import Searchable from "../../components/controls/select/CustomSelect";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import { debounce } from 'lodash';
import styles from './TaskDependencies.module.scss';
import Skeleton from "react-loading-skeleton";
import { dateOnlyFormatted } from '../../utils/systemSettings';
const TaskDependencies = (props) => {

  const {
    projectId,
    scope,
    taskDependencies = [],
    onDependenciesChange,
    disabled = false,
    loading = false,
  } = props;

  const settings = JSON.parse(localStorage.getItem("settings"));
  const [showSelector, setShowSelector] = useState(false);
  const [scopes, setScopes] = useState(scope ? [scope] : []);
  const [tasks, setTasks] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [selectedScope, setSelectedScope] = useState(scope);

  const [newDependency, setNewDependency] = useState({
    taskId: null,
    taskName: '',
    taskStartDate: null,
    taskEndDate: null,
    dependencyType: 0
  });

  const [editDependency, setEditDependency] = useState({
    taskId: null,
    taskName: '',
    taskStartDate: null,
    taskEndDate: null,
    dependencyType: 0
  });

  const taskDependencyTypes = [
    { name: 'Finish To Start (FS)', shortName: 'FS', value: 0 },
    { name: 'Start To Start (SS)', shortName: 'SS', value: 1 },
  ];

  useEffect(() => {
    if (taskDependencies && taskDependencies.length > 0) {
      const formattedDependencies = taskDependencies.map((dependency) =>
      (
        {
          taskId: dependency.task.id,
          taskName: dependency.task.name,
          taskStartDate: dependency.task.startDate,
          taskEndDate: dependency.task.endDate,
          dependencyType: dependency.dependencyType
        }
      )
      );

      setDependencies(formattedDependencies);
      onChange(formattedDependencies);
    }

  }, [taskDependencies]);


  const scopeSearchdebounced = useRef(
    debounce((value, params) => searchScopeHandler(value, params), 500)
  );

  const scopeSearch = useCallback((value, params) => {
    scopeSearchdebounced.current(value, params);
  }, []);

  const searchScopeHandler = (value, params) => {
    if (!params.projectId) {
      setScopes([]);
      return;
    }
    searchService
      .Scopes(value, params)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setScopes(transformedArray);
      })
      .catch(() => {
        setScopes([]);
      });
  };

  const taskSearchdebounced = useRef(
    debounce((value, params) => searchTaskHandler(value, params), 500)
  );

  const taskSearch = useCallback((value, params) => {
    taskSearchdebounced.current(value, params);
  }, []);

  const searchTaskHandler = (value, params) => {
    if (!params.projectId && !params.scopeId) {
      setTasks([]);
      return;
    }
    searchService
      .Tasks(value, params)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
          startDate: obj.startDate,
          endDate: obj.endDate,
        }));
        setTasks(transformedArray);
      })
      .catch(() => {
        setTasks([]);
      });
  };

  const updateNewDependency = (field, value) => {
    setNewDependency(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearNewDependency = () => {
    setNewDependency({
      taskId: null,
      taskName: '',
      taskStartDate: null,
      taskEndDate: null,
      dependencyType: 0
    });
  };

  const updateEditDependency = (field, value) => {
    setEditDependency(prev => ({
      ...prev,
      [field]: value,
    }));
  };
  const clearEditDependency = () => {
    setEditDependency({
      taskId: null,
      taskName: '',
      taskStartDate: null,
      taskEndDate: null,
      dependencyType: 0
    });
  };

  const onChange = (dependencies) => {
    onDependenciesChange?.(dependencies.map((dependency) => (
      {
        id: dependency.taskId,
        dependencyType: dependency.dependencyType,
        startDate: dependency.taskStartDate,
        endDate: dependency.taskEndDate
      }
    )))
  };

  const handleAddClick = () => {
    setShowSelector(true);
    clearNewDependency();
  };

  const handleSaveDependency = () => {
    if (!newDependency.taskId) return;

    const updatedDependencies = [...dependencies, newDependency];
    setDependencies(updatedDependencies);
    onChange(updatedDependencies);

    clearNewDependency();
    setShowSelector(false);
  };

  const handleCancelAdd = () => {
    setShowSelector(false);
    clearNewDependency();
  };

  const handleDeleteDependency = (id) => {
    const updatedDependencies = dependencies.filter(dep => dep.taskId !== id);
    setDependencies(updatedDependencies);
    onChange(updatedDependencies);
  };

  const handleEditDependency = (dependency) => {
    setEditDependency({
      taskId: dependency.taskId,
      taskName: dependency.taskName,
      dependencyType: dependency.dependencyType
    });
  };

  const handleSaveEdit = () => {
    if (!editDependency.taskId) return;

    const updatedDependencies = dependencies.map(dep =>
      dep.id === editDependency.taskId
        ? { ...dep, taskId: editDependency.taskId, type: editDependency.dependencyType }
        : dep
    );

    setDependencies(updatedDependencies);
    onChange(updatedDependencies);
    clearEditDependency();
  };

  const handleCancelEdit = () => {
    clearEditDependency();
  };

  const handleScopeChange = (newScopeId) => {
    setSelectedScope(newScopeId);
  };


  const getTypeName = (type, isShort = false) => {
    const dependencyType = taskDependencyTypes.find(t => t.value === type)
    return (isShort ? dependencyType?.shortName : dependencyType?.name) || '';
  };

  const getDependencyDate = (dependency) => {
    if(!dependency) return '';

    if(dependency?.dependencyType === 0 && dependency?.taskEndDate) {
      return ` | ${dateOnlyFormatted(settings, dependency.taskEndDate)}`;
    } 
    
    if(dependency?.dependencyType === 1 && dependency?.taskStartDate) {
      return ` | ${dateOnlyFormatted(settings, dependency.taskStartDate)}`;
    }

    return'';
  };

  return (
    loading ? (
      <div className={styles.container}>
        <Skeleton height={80} baseColor="#e0e0e0" highlightColor="#ECE7FD" />

      </div>
    ) : (
      <div className={styles.container}>
        {!showSelector && (

          // <div className="AddFrame AddFrame_sm" onClick={handleAddClick}>
          //     <div className="AddFrame_Icon">
          //         <AddTaskSvg />
          //     </div>
          //     <p className="text-xsm-400">Add dependencies</p><br/>
          // </div>

          !disabled && (<button
            onClick={handleAddClick}
            className={styles.addButton}
          >
            <div className={styles.addButtonIcon}>
              <Plus size={12} strokeWidth={2} />
            </div>
            <div className={styles.addButtonContent}>
              <div className={styles.addButtonTitle}>
                Add dependencies
              </div>
              <div className={styles.addButtonSubtitle}>
                Configure task dependencies and relationships
              </div>
            </div>
          </button>)
        )}

        {!disabled && showSelector && (
          <div className={styles.selectorCard}>
            <div className={styles.selectorHeader}>
              <div>
                <p className={styles.selectorTitle}>Add Task Dependency</p>
                <p className={styles.selectorSubtitle}>Configure the dependency relationship for this task</p>
              </div>
              <div className={styles.selectorActions}>
                <button
                  onClick={handleSaveDependency}
                  disabled={!newDependency.taskId}
                  className={styles.saveButton}
                  title="Save dependency"
                >
                  <Check size={16} strokeWidth={2.5} />
                </button>
                <button
                  onClick={handleCancelAdd}
                  className={styles.cancelButton}
                  title="Cancel"
                >
                  <X size={16} strokeWidth={2.5} />
                </button>
              </div>
            </div>

            <div className={styles.selectorGrid}>
              <div className={styles.fieldGroup}>
                <Searchable
                  label="Scope / Milestone "
                  searchText={(e) =>
                    scopeSearch(e, { projectId })
                  }
                  SelectText="scope"
                  options={scopes}
                  defaultVal={selectedScope}
                  onChange={(e) => {
                    setSelectedScope(e);
                    clearNewDependency();
                  }}
                />
              </div>

              <div className={styles.fieldGroup}>
                <Searchable
                  label="Task"
                  searchText={(e) =>
                    taskSearch(e, {
                      projectId,
                      scopeId: selectedScope && selectedScope.value,
                    })
                  }
                  SelectText="task"
                  options={tasks.filter(task => !dependencies.some(dep => dep.taskId === task.value))}
                  defaultVal={newDependency?.taskId ? { label: newDependency?.taskName, value: newDependency?.taskId } : null}
                  onChange={(e) => {
                    updateNewDependency("taskId", e ? e.value : null);
                    updateNewDependency("taskName", e ? e.label : '');
                    updateNewDependency("taskStartDate", e ? e.startDate : null);
                    updateNewDependency("taskEndDate", e ? e.endDate : null);
                  }
                  }
                />
              </div>

              <div className={styles.fieldGroup}>
                <DropDown
                  label="Type"
                  options={taskDependencyTypes}
                  defaultVal={taskDependencyTypes.find(t => t.value === newDependency.dependencyType)?.name || 'Finish To Start (FS)'}
                  onChange={(e) => {
                    const type = taskDependencyTypes.find(t => t.value === e)?.value ?? 0;
                    updateNewDependency("dependencyType", type);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {dependencies && dependencies.length > 0 && (
          <div className={styles.dependenciesList}>
            {dependencies.map((dependency) => (
              <div key={dependency.taskId} className={styles.dependencyCard}>
                <div className={styles.dependencyContent}>
                  <div className={styles.dependencyInfo}>
                    <div className={styles.dependencyName}>
                      <p className='text-truncate'>{dependency.taskName}</p>
                    </div>

                  </div>

                  <div className={styles.dependencyActions}>
                    <div className={`${styles.dependencyType} ${dependency.dependencyType === 1 ? styles.startToStart : styles.finishToStart}`}>
                      {`${getTypeName(dependency.dependencyType, true)}${getDependencyDate(dependency)}`} 
                    </div>
                    {/* <button
                      onClick={() => handleEditDependency(dependency)}
                      className={styles.editButton}
                      title="Edit dependency"
                    >
                      <Pencil size={16} strokeWidth={2} />
                    </button> */}
                    {!disabled && (
                      <button
                        onClick={() => handleDeleteDependency(dependency.taskId)}
                        className={styles.deleteButton}
                        title="Delete dependency"
                      >
                        <Trash2 size={16} strokeWidth={2} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )

  );
};

export default TaskDependencies;